import { useStore } from '@nanostores/react';
import { useMemo } from 'react';
import type { IconName } from '@customerio/pluma-icons';
import { useActiveBundle } from '@/context/active-bundle';
import { IconButton } from '@/layouts/authenticated/components/top-navigation/components/icon-button';
import { useAnalytics } from '@/services/analytics';
import { useCustomerFeedback } from '../use-customer-feedback';
import { feedbackButtonsAtom, type FeedbackButton } from '../registry';

/**
 * Renders every feedback button registered for the active app in a single consistent
 * location in the top navigation. Each button opens the shared feedback modal via
 * `useCustomerFeedback`, so all surfaces route through the same modal and endpoint.
 */
export function FeedbackButtons() {
  const registrations = useStore(feedbackButtonsAtom);
  const activeApp = useActiveBundle();
  const showFeedbackModal = useCustomerFeedback();
  const { track } = useAnalytics();

  const buttons = useMemo<FeedbackButton[]>(() => {
    const result: FeedbackButton[] = [];
    for (const [, entry] of registrations) {
      if (entry.app === activeApp) {
        result.push(entry);
      }
    }
    return result;
  }, [registrations, activeApp]);

  if (buttons.length === 0) {
    return null;
  }

  return (
    <>
      {buttons.map((button) => (
        <IconButton
          key={`${button.feature ?? 'general'}-${button.label}`}
          icon={
            // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
            (button.icon as IconName) ?? 'chat'
          }
          label={button.label}
          showLabel={true}
          size="md"
          onClick={() => {
            // Registrations that opt into tracking keep the Mixpanel events
            // their pre-registry feedback flow fired (CON-7986). Payload
            // parity with hydra's `DesignStudioFeedbackLink` port: the shell
            // has no Journeys-embed handshake, so the journeys ids are null.
            const { tracking } = button;
            if (tracking != null) {
              void track('userFeedback', 'clicked', {
                label: tracking.label,
                metadata: tracking.metadata ?? null,
                journeys_workspace_id: null,
                journeys_template_id: null,
              });
            }

            void showFeedbackModal({
              feature: button.feature,
              initialFeedback: button.initialFeedback,
              message: button.message,
              onSubmit:
                tracking == null
                  ? undefined
                  : (values) => {
                      void track('userFeedback', 'submitted', {
                        label: tracking.label,
                        metadata: tracking.metadata ?? null,
                        feedback: values.feedback ?? null,
                        journeys_workspace_id: null,
                        journeys_template_id: null,
                      });
                    },
            });
          }}
        />
      ))}
    </>
  );
}
